import { LayerConfig } from "fond/types/ProjectLayerConfig";
import { ScenarioConfiguration, VersionScenarioConfiguration } from "fond/types/version";

import { FormData } from "./SetupForm";

export const ignoreLayerOption = {
  Type: "LAYER",
  ID: undefined,
  Key: undefined,
  Label: "Ignore Layer",
} as unknown as LayerConfig;

export interface C2SLayersConfiguration {
  physical_elements?: string[] | null;
  network_elements?: string[] | null;
  edge_elements?: string[] | null;
  node_elements?: string[] | null;
  layer_name_mapping?: Record<string, string> | null;
  combine_layers?: Record<string, string[]> | null;
}

export const resolveLayerConfigurationNameFromFilename = (layersConfiguration: C2SLayersConfiguration, fileName: string): string => {
  return layersConfiguration?.layer_name_mapping?.[fileName] || fileName;
};

export const resolveLayerConfigurationInputLayers = (layersConfiguration: C2SLayersConfiguration): Set<string> => {
  let layers = new Set<string>();
  layersConfiguration?.physical_elements?.forEach((el) => layers.add(el));
  layersConfiguration?.network_elements?.forEach((el) => layers.add(el));
  layersConfiguration?.edge_elements?.forEach((el) => layers.add(el));
  layersConfiguration?.node_elements?.forEach((el) => layers.add(el));

  // The BOM processes layer configurations by (i) renaming, and then (ii) combining layers. We're trying
  // to do the reverse here, so we first split up combined layers and then revert back to the original name.
  if (layersConfiguration?.combine_layers) {
    layers = new Set(Array.from(layers).flatMap((layer) => layersConfiguration?.combine_layers?.[layer] ?? [layer]));
  }

  if (layersConfiguration?.layer_name_mapping) {
    const reverseNameMap = new Map<string, string>(
      Object.entries(layersConfiguration.layer_name_mapping).map(([original, renamed]) => [renamed, original])
    );
    layers = new Set(Array.from(layers).map((layer) => reverseNameMap.get(layer) ?? layer));
  }

  return layers;
};

/**
 * Filters out null/undefined/ignoreLayerOption values from a layer mapping.
 */
export const filterLayerMappings = (mapping: Record<string, LayerConfig | null>): Record<string, LayerConfig> => {
  return Object.fromEntries(Object.entries(mapping).filter(([_, value]) => value != null && value !== ignoreLayerOption)) as Record<
    string,
    LayerConfig
  >;
};

export const convertLayerMappings = (mapping: Record<string, LayerConfig | null>): FormData["Inputs"]["ToolLayersConfiguration"]["Mappings"] => {
  return Object.entries(filterLayerMappings(mapping)).map(([key, { Key }]) => ({
    Name: key,
    Layer: { LayerKey: Key },
  }));
};

/**
 * Converts a VersionScenarioConfiguration to FormData format.
 * Specifically:
 * 1) Parses Inputs.ToolLayersConfiguration.LayersConfiguration.Data from JSON string to object
 * 2) Stringifies ToolConfig.CostToServe.AdditionalSettings from object to JSON string
 * 3) Stringifies ToolConfig.DesignPhasePartitioner.AdditionalSettings from object to JSON string
 * 4) Adds Files field for form compatibility
 */
export const convertConfigToFormData = (config: VersionScenarioConfiguration): FormData => {
  return {
    Files: {
      Artifacts: [],
      Occupancy: null,
    },
    Inputs: {
      ...config.Inputs,
      ToolLayersConfiguration: {
        ...config.Inputs.ToolLayersConfiguration,
        LayersConfiguration: {
          ...config.Inputs.ToolLayersConfiguration.LayersConfiguration,
          Data: JSON.parse(config.Inputs.ToolLayersConfiguration.LayersConfiguration.Data),
        },
      },
    },
    ToolConfigurations: {
      ...config.ToolConfigurations,
      CostToServe: {
        ...config.ToolConfigurations.CostToServe,
        AdditionalSettings: JSON.stringify(config.ToolConfigurations.CostToServe.AdditionalSettings), // TODO
      },
      DesignPhasePartitioner: {
        ...config.ToolConfigurations.DesignPhasePartitioner,
        AdditionalSettings: JSON.stringify(config.ToolConfigurations.DesignPhasePartitioner.AdditionalSettings), // TODO
      },
    },
  };
};

/**
 * Converts FormData to ScenarioConfiguration format.
 * Specifically:
 * 1) Stringifies Inputs.ToolLayersConfiguration.LayersConfiguration.Data from object to JSON string
 * 2) Parses ToolConfig.CostToServe.AdditionalSettings from JSON string to object
 * 3) Parses ToolConfig.DesignPhasePartitioner.AdditionalSettings from JSON string to object
 * 4) Removes Files field (not part of ScenarioConfiguration)
 */
export const convertFormDataToConfig = (formData: FormData): ScenarioConfiguration => {
  return {
    Inputs: {
      ...formData.Inputs,
      ToolLayersConfiguration: {
        ...formData.Inputs.ToolLayersConfiguration,
        LayersConfiguration: {
          ...formData.Inputs.ToolLayersConfiguration.LayersConfiguration,
          Data: JSON.stringify(formData.Inputs.ToolLayersConfiguration.LayersConfiguration.Data),
        },
      },
    },
    ToolConfigurations: {
      ...formData.ToolConfigurations,
      CostToServe: {
        ...formData.ToolConfigurations.CostToServe,
        AdditionalSettings: JSON.parse(formData.ToolConfigurations.CostToServe.AdditionalSettings), // TODO
      },
      DesignPhasePartitioner: {
        ...formData.ToolConfigurations.DesignPhasePartitioner,
        AdditionalSettings: JSON.parse(formData.ToolConfigurations.DesignPhasePartitioner.AdditionalSettings), // TODO
      },
    },
  };
};
